import { useState } from "react";
import { ApiError } from "@/api";
import { Button } from "@/components/ui";
import type { RetryResult } from "@/types/issuePipeline";

const OUTCOMES: Record<RetryResult["result"], string> = {
  tc_created: "TC 됨",
  not_a_problem: "문제 아님",
  error: "여전히 오류",
};

/** The one result line of a [다시 시도]; kept by the caller, so it outlives the button and a reload. */
export type RetryLine = { text: string; error: boolean };

export function retryLine(result: RetryResult): RetryLine {
  return { text: `${OUTCOMES[result.result]} · ${result.message}`, error: result.result === "error" };
}

/**
 * Why a request got no answer, in plain words. SAMS's own refusal (a coded error) is shown as is;
 * anything else — a proxy's 502/504 page, an empty reply, the 660 s abort, a dropped connection —
 * left the server running, so the reviewer is told to look again rather than shown raw text.
 */
export function retryFailure(error: unknown): RetryLine {
  if (error instanceof ApiError && error.code) {
    return { text: `다시 시도하지 못했습니다 · ${error.code}: ${error.message}`, error: true };
  }
  return {
    text: "응답을 받지 못했습니다(시간 초과 또는 연결 끊김). 서버에서는 계속 실행 중일 수 있으니 잠시 뒤 목록을 새로 고쳐 결과를 확인하세요.",
    error: true,
  };
}

/**
 * [다시 시도] of an error row (user decision 2026-10-21): the server runs it at once —
 * 1.1차 again or the TC save again. While it runs the button stays disabled, so one
 * click is one retry; ``onSettled`` gets the result line (also on a failed request).
 */
export function RetryButton({ run, onSettled }: {
  run: () => Promise<RetryResult>;
  onSettled: (line: RetryLine, result: RetryResult | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const retry = async () => {
    if (busy) return;
    setBusy(true);
    let line: RetryLine;
    let result: RetryResult | null = null;
    try {
      result = await run();
      line = retryLine(result);
    } catch (err) {
      line = retryFailure(err);
    }
    setBusy(false);
    onSettled(line, result);
  };
  return (
    <div className="grid justify-items-start gap-1">
      <Button variant="outline" size="sm" disabled={busy} onClick={() => void retry()}>
        {busy ? "다시 시도하는 중…" : "다시 시도"}
      </Button>
      {busy && <p role="status" className="text-xs text-muted-foreground">판정과 저장을 지금 실행합니다. 몇 분 걸릴 수 있습니다.</p>}
    </div>
  );
}

/** A kept result line. */
export function RetryNote({ line, label }: { line: RetryLine; label?: string }) {
  return (
    <p role="status" aria-label="다시 시도 결과" className={`text-xs ${line.error ? "text-destructive" : "text-foreground"}`}>
      {label ? `${label} · ` : ""}{line.text}
    </p>
  );
}
